import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  // Note: Material-UI doesn't support responsive z-index in theme
  // We'll handle this through component-level overrides instead
  palette: {
    primary: {
      main: '#2196f3',
      light: '#64b5f6',
      dark: '#1976d2',
    },
    secondary: {
      main: '#1e2a38',
      light: '#455a64',
      dark: '#263238',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
    success: {
      main: '#4caf50',
    },
    warning: {
      main: '#ff9800',
    },
    error: {
      main: '#f44336',
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
    h1: {
      fontSize: '2rem',
      fontWeight: 600,
      '@media (max-width:600px)': {
        fontSize: '1.75rem',
      },
    },
    h2: {
      fontSize: '1.75rem',
      fontWeight: 600,
      '@media (max-width:600px)': {
        fontSize: '1.5rem',
      },
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 500,
      '@media (max-width:600px)': {
        fontSize: '1.25rem',
      },
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
      '@media (max-width:600px)': {
        fontSize: '0.875rem',
      },
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.43,
      '@media (max-width:600px)': {
        fontSize: '0.8125rem',
      },
    },
  },
  spacing: 8,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          // PWA-specific styles
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
          WebkitTapHighlightColor: 'transparent',
          // Safe area support for devices with notches
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
          // Custom CSS variables for mobile
          '--vh': '1vh',
          minHeight: 'calc(var(--vh, 1vh) * 100)',
          '@media (max-width:600px)': {
            fontSize: '16px', // Prevent zoom on iOS
          },
        },
        '*': {
          boxSizing: 'border-box',
        },
        // Disable text selection on mobile for app-like feel
        '@media (max-width:768px)': {
          '*': {
            userSelect: 'none',
            WebkitUserSelect: 'none',
          },
          'input, textarea': {
            userSelect: 'text',
            WebkitUserSelect: 'text',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 500,
          minHeight: 44, // Touch-friendly minimum height
          '@media (max-width:600px)': {
            minHeight: 48, // Larger touch targets on mobile
            fontSize: '0.875rem',
            // Enhanced touch responsiveness
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)',
            WebkitTouchCallout: 'none',
            userSelect: 'none',
            cursor: 'pointer',
            // Improve touch feedback
            '&:active': {
              transform: 'scale(0.98)',
              transition: 'transform 0.1s ease',
            },
          },
        },
        contained: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          },
          '&:active': {
            boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
          },
        },
        outlined: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            minHeight: 44,
            // Ensure text fields are clickable
            position: 'relative',
            zIndex: 1,
            '@media (max-width:600px)': {
              minHeight: 48,
              fontSize: '16px', // Prevent zoom on iOS
            },
          },
          '& .MuiInputLabel-root': {
            '@media (max-width:600px)': {
              fontSize: '16px',
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          '@media (max-width:600px)': {
            minHeight: 'unset',
            height: 'auto',
            fontSize: '16px',
            padding: '12px 14px',
          },
        },
      },
      defaultProps: {
        MenuProps: {
          sx: {
            // Ensure dropdown menus appear above dialogs on mobile
            '@media (max-width:900px)': {
              zIndex: 1700,
            },
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '@media (max-width:600px)': {
              minHeight: 'unset',
              height: 'auto',
            },
          },
          '& .MuiSelect-select': {
            '@media (max-width:600px)': {
              minHeight: 'unset',
              height: 'auto',
              padding: '12px 14px',
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            minHeight: 'unset',
            height: 'auto',
            '&.MuiInputBase-sizeSmall': {
              minHeight: 'unset',
              height: 'auto',
            },
          },
        },
        input: {
          '@media (max-width:600px)': {
            padding: '12px 14px',
            height: 'auto',
            '&.MuiInputBase-inputSizeSmall': {
              padding: '10px 14px',
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            padding: '8px 10px', // Reduced vertical padding, increased horizontal for better spacing
            fontSize: '0.8125rem',
          },
        },
        head: {
          fontWeight: 'bold',
          color: '#666',
          backgroundColor: '#fafafa',
          '@media (max-width:600px)': {
            fontSize: '0.75rem',
            padding: '8px 10px', // Consistent with body cells
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            '&::-webkit-scrollbar': {
              height: 4,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#c1c1c1',
              borderRadius: 2,
            },
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1e2a38',
          color: '#ffffff',
          '@media (max-width:600px)': {
            width: '280px',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          '@media (max-width:600px)': {
            paddingTop: 'env(safe-area-inset-top)',
          },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: 64,
          '@media (max-width:600px)': {
            minHeight: 56,
            paddingLeft: 16,
            paddingRight: 16,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            padding: 12, // Larger touch targets
            minWidth: 44,
            minHeight: 44,
            // Enhanced touch responsiveness
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)',
            WebkitTouchCallout: 'none',
            userSelect: 'none',
            cursor: 'pointer',
            // Improve touch feedback
            '&:active': {
              transform: 'scale(0.95)',
              transition: 'transform 0.1s ease',
            },
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            padding: 12,
            minWidth: 44,
            minHeight: 44,
            // Enhanced touch responsiveness
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)',
            WebkitTouchCallout: 'none',
            userSelect: 'none',
            cursor: 'pointer',
            // Improve touch feedback
            '&:active': {
              transform: 'scale(0.95)',
              transition: 'transform 0.1s ease',
            },
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            minHeight: 48,
            padding: '12px 16px',
            // Enhanced touch responsiveness
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)',
            WebkitTouchCallout: 'none',
            userSelect: 'none',
            cursor: 'pointer',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          '@media (max-width:600px)': {
            borderRadius: 8,
            margin: '8px',
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            padding: 16,
            '&:last-child': {
              paddingBottom: 16,
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          // Mobile-specific z-index to appear above bottom navigation (1500)
          '@media (max-width:900px)': {
            zIndex: 1600,
          },
        },
        paper: {
          borderRadius: 12,
          '@media (max-width:600px)': {
            margin: 16,
            width: 'calc(100% - 32px)',
            maxHeight: 'calc(100% - 32px)',
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            fontSize: '1.25rem',
            padding: '16px 16px 8px',
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            padding: '8px 16px',
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            padding: '8px 16px 16px',
          },
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            left: 16,
            right: 16,
            bottom: 16,
          },
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          height: 64,
          paddingBottom: 'env(safe-area-inset-bottom)',
          '@media (max-width:600px)': {
            height: 72, // Match the component's height
          },
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          minWidth: 'auto',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          '@media (max-width:600px)': {
            fontSize: '0.6rem', // Match the component's font size
            minHeight: '60px',
            padding: '6px',
          },
        },
      },
    },
    // Custom component for mobile-specific styles
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            paddingLeft: 16,
            paddingRight: 16,
          },
        },
      },
    },
  },
});

export default theme;
